import {
	Box,
	Card,
	Group,
	Skeleton,
	Text,
	ThemeIcon,
	Title,
} from "@mantine/core";
import { BarChart3 } from "lucide-react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	LabelList,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { useSnapshot } from "valtio";
import { useIsDark } from "@/hooks/useIsDark";
import { useTranslate } from "@/hooks/useTranslate";
import { i18nStore } from "@/store/i18n";
import type { SectorData } from "./demografi.types";
import { CategoryTick } from "./demografi-axis-tick";
import {
	BAR_VALUE_MARGIN_RIGHT,
	SECTOR_AXIS_WIDTH,
	SECTOR_CHART_MIN_HEIGHT,
	SECTOR_LABEL_MAX_CHARS,
} from "./demografi-layout.const";

interface DemografiSectorCardProps {
	sektorData: SectorData[];
	loading: boolean;
}

export const DemografiSectorCard = ({
	sektorData,
	loading,
}: DemografiSectorCardProps) => {
	const t = useTranslate();
	const dark = useIsDark();
	const { tampilkanGrid } = useSnapshot(i18nStore);

	return (
		<Card
			data-ai-target="demografi.sektor"
			p="md"
			radius="xl"
			withBorder
			bg={dark ? "#1E293B" : "white"}
			style={{
				borderColor: dark ? "#334155" : "white",
				boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
				display: "flex",
				flexDirection: "column",
			}}
			h="100%"
		>
			<Group gap="xs" mb="md">
				<ThemeIcon
					color="darmasaba-navy.7"
					variant="filled"
					size="sm"
					radius="sm"
				>
					<BarChart3 size={14} />
				</ThemeIcon>
				<Title order={4} c={dark ? "white" : "gray.9"}>
					{t.demografiPekerjaan.sektorUnggulan}
				</Title>
			</Group>
			{loading ? (
				<Skeleton style={{ flex: 1 }} radius="md" />
			) : sektorData.length === 0 ? (
				<Group justify="center" align="center" style={{ flex: 1 }}>
					<Text size="sm" c="dimmed">
						Belum ada data sektor unggulan.
					</Text>
				</Group>
			) : (
				// Mengisi sisa tinggi kartu: saat tinggi baris dipatok, chart ikut
				// melar sehingga tidak ada lagi ruang kosong di bawahnya.
				<Box style={{ flex: 1, minHeight: SECTOR_CHART_MIN_HEIGHT }}>
					<ResponsiveContainer width="100%" height="100%">
						<BarChart
							data={sektorData}
							layout="vertical"
							margin={{ right: BAR_VALUE_MARGIN_RIGHT }}
						>
							{tampilkanGrid && (
								<CartesianGrid
									strokeDasharray="3 3"
									horizontal={false}
									stroke={dark ? "#334155" : "#e5e7eb"}
								/>
							)}
							<XAxis
								type="number"
								axisLine={false}
								tickLine={false}
								tick={{
									fill: dark ? "#E2E8F0" : "#374151",
									fontSize: 12,
								}}
							/>
							<YAxis
								type="category"
								dataKey="sektor"
								axisLine={false}
								tickLine={false}
								interval={0}
								width={SECTOR_AXIS_WIDTH}
								tick={
									<CategoryTick
										fill={dark ? "#E2E8F0" : "#374151"}
										fontSize={11}
										maxChars={SECTOR_LABEL_MAX_CHARS}
									/>
								}
							/>
							<Tooltip
								contentStyle={{
									backgroundColor: dark ? "#1E293B" : "white",
									borderColor: dark ? "#334155" : "#e5e7eb",
									borderRadius: "8px",
								}}
								itemStyle={{ color: dark ? "#E2E8F0" : "#374151" }}
								labelStyle={{ color: dark ? "#E2E8F0" : "#374151" }}
							/>
							<Bar
								dataKey="value"
								fill="#396aaaff"
								radius={[0, 8, 8, 0]}
								maxBarSize={40}
							>
								{sektorData.map((entry) => (
									<Cell key={`cell-${entry.sektor}`} fill="#396aaaff" />
								))}
								<LabelList
									dataKey="value"
									position="right"
									style={{
										fill: dark ? "#E2E8F0" : "#374151",
										fontSize: 11,
									}}
								/>
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				</Box>
			)}
		</Card>
	);
};
